import { IconButton, Tooltip } from '@mui/material';
import { HighlightAlt as Area } from '@mui/icons-material';
import { MeasureAreaMode, ViewMode } from '@nebula.gl/edit-modes';
import { EditableGeoJsonLayer } from '@nebula.gl/layers';
import clsx from 'clsx';
import { MapContext } from 'components/maps/MapContext';
import React, { memo, useCallback, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MapTool } from '../const';
import useLayersToolbarStyles from '../styles';
import { AreaToolProps } from './types';
import { useHotKey, useUnitPreference } from 'kineticUtils/hooks';
import { EditableGeoJsonLayerProps } from 'components/maps/BaseMap';

export const AreaTool: React.FunctionComponent<AreaToolProps> = () => {
  const { t } = useTranslation('app');
  const classes = useLayersToolbarStyles();
  const { activeMapTool, setExtraDeckGlLayers, setActiveMapTool } = useContext(MapContext);

  const [mode, setMode] = React.useState(() => MeasureAreaMode);
  const unitPreference = useUnitPreference();
  const isActive = activeMapTool === MapTool.AREA;

  useHotKey({
    key: 'a',
    alt: true,
    ctrl: true,
    callback: () => setActiveMapTool(MapTool.AREA),
  });

  const formatTooltip = useCallback(
    (area) => {
      return unitPreference.unit === 'imperial'
        ? `${(area * 3.861e-7).toFixed(1)} ${t('sqMi')}`
        : `${(area / 1000000).toFixed(1)} ${t('sqKm')}`;
    },
    [t, unitPreference.unit]
  );

  useEffect(() => {
    setExtraDeckGlLayers((prev) => {
      const toSave = [...prev];
      const layer = new EditableGeoJsonLayer({
        id: 'area-layer',
        modeConfig: {
          formatTooltip: formatTooltip,
        },
        mode: !isActive ? ViewMode : mode,
        order: 4,
      } as EditableGeoJsonLayerProps);

      const index = toSave.findIndex((layer) => layer.id === 'area-layer');
      if (index > -1) {
        toSave.splice(index, 1);
      }
      toSave.push(layer);
      return toSave;
    });
  }, [isActive, setExtraDeckGlLayers, mode, formatTooltip]);

  return (
    <>
      <Tooltip title={t('areaTool')}>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setActiveMapTool(MapTool.AREA);
            setMode(() => MeasureAreaMode);
          }}
          className={clsx(classes.button, { [classes.buttonActive]: isActive })}
          sx={{ padding: '9px' }}
        >
          <Area fontSize='small' />
        </IconButton>
      </Tooltip>
    </>
  );
};

export default memo(AreaTool);
